/**
 * ModulePicker — `+ FAB` add-module flow for V2 workspace canvas.
 *
 * Cycle 101 sprint-3 Task 3.6 (sprint.md:310).
 *
 * UX contract:
 *   * A fixed-position `+ FAB` at bottom-center of the shell. `aria-label="Add module"`
 *     for sprint-3 AC-9 a11y clause.
 *   * FAB click opens a modal dialog with a category-filter sidebar + a list
 *     of all modules in the selected category. Clicking a module appends a
 *     new `ModulePlacement` at the bottom of the current-mode layout, using
 *     the registry's `defaultSize: { w, h }` (cycle-109 sprint-204 S2.2 reshape).
 *   * Placement id generated via timestamp + random suffix (stable enough
 *     for the lifetime of a browser session; not cryptographically unique).
 *   * Closes on selection, on ESC, or on backdrop click.
 *
 * The picker DOES NOT own the layout — it receives `addPlacement` via
 * props so the single `useModeCanvasLayout` instance in the V2 shell owns
 * the write. Any consumer that wants to drive `+ FAB` outside the shell
 * can plug in its own `addPlacement`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MODULE_CATEGORY_ORDER,
  MODULE_REGISTRY,
  type ModuleCategory,
  type ModuleRegistryEntry,
} from '../../lib/moduleRegistry';
import { CATEGORY_VISUAL } from './PlaceholderModule';
import type { ModulePlacement } from '../../lib/modePresets';

// ── Categories with labels ──────────────────────────────────────────────

// Canonical six-set labels per moduleRegistry.jsx:48-55 (cycle-109 sprint-204 S2.2).
// CATEGORY_VISUAL (from PlaceholderModule) carries the canonical labels + glyphs;
// this map exists for the picker sidebar's slightly-different display label
// (CATEGORY_VISUAL.label is the canonical short form used in placeholder kickers).
const CATEGORY_LABELS: Record<ModuleCategory, string> = {
  echelon: 'Echelon',
  geopolitical: 'Geopolitical',
  natural: 'Natural events',
  economy: 'Economy & markets',
  infrastructure: 'Infrastructure & transport',
  other: 'Other',
};

// ── Pure helpers (exported for tests) ───────────────────────────────────

/** Compute the `y` row for a new placement: one row below the last-used row. */
export function computeNextY(placements: readonly ModulePlacement[]): number {
  let maxEnd = 0;
  for (const p of placements) {
    const end = p.y + p.h;
    if (end > maxEnd) maxEnd = end;
  }
  return maxEnd;
}

/** Generate a unique placement id.
 *
 * DISS-302-R3 fix: prefer `crypto.randomUUID()` (zero collision, widely
 * available in modern browsers + Node 16+). Fallback to the Date-based
 * scheme only if the crypto API is unavailable (very old browsers,
 * non-secure contexts). The fallback retains its original collision
 * profile — low probability in practice but non-zero at high FAB rate. */
export function generatePlacementId(): string {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.randomUUID === 'function'
  ) {
    return `pl-${globalThis.crypto.randomUUID()}`;
  }
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `pl-${ts}-${rand}`;
}

/** Build a new placement for the given module entry at the bottom of the layout. */
export function buildNewPlacement(
  entry: ModuleRegistryEntry,
  existing: readonly ModulePlacement[]
): ModulePlacement {
  return {
    id: generatePlacementId(),
    moduleId: entry.id,
    x: 0,
    y: computeNextY(existing),
    w: entry.defaultSize.w,
    h: entry.defaultSize.h,
  };
}

/**
 * Stable-ordered list of categories that have at least one module.
 *
 * Sprint-204 S2.5: orders by canonical `MODULE_CATEGORY_ORDER` (echelon →
 * geopolitical → natural → economy → infrastructure → other) instead of
 * registry-declaration order. This matches the canonical chip-row order in
 * moduleRegistry.jsx:48-55 + PlaceholderModule kickers.
 */
export function categoriesWithModules(): readonly ModuleCategory[] {
  const present = new Set<ModuleCategory>();
  for (const entry of MODULE_REGISTRY) {
    present.add(entry.category);
  }
  return MODULE_CATEGORY_ORDER.filter((cat) => present.has(cat));
}

// ── Component ───────────────────────────────────────────────────────────

export interface ModulePickerProps {
  readonly placements: readonly ModulePlacement[];
  readonly onAddPlacement: (placement: ModulePlacement) => void;
  readonly className?: string;
}

export function ModulePicker({
  placements,
  onAddPlacement,
  className,
}: ModulePickerProps) {
  const [open, setOpen] = useState(false);
  const categories = useMemo(() => categoriesWithModules(), []);
  const [activeCategory, setActiveCategory] = useState<ModuleCategory>(
    categories[0] ?? 'echelon'
  );

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleSelect = useCallback(
    (entry: ModuleRegistryEntry) => {
      const placement = buildNewPlacement(entry, placements);
      onAddPlacement(placement);
      setOpen(false);
    },
    [onAddPlacement, placements]
  );

  // ESC closes the modal.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const modulesInCategory = useMemo(
    () => MODULE_REGISTRY.filter((m) => m.category === activeCategory),
    [activeCategory]
  );

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        aria-label="Add module"
        className={className}
        style={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 52,
          height: 52,
          borderRadius: 26,
          background: 'var(--cd-fg)',
          color: 'var(--cd-bg)',
          border: 'none',
          boxShadow: 'var(--cd-shadow-2)',
          cursor: 'pointer',
          fontSize: 24,
          fontWeight: 300,
          lineHeight: 1,
          zIndex: 20,
          fontFamily: 'var(--cd-font-sans)',
        }}
      >
        +
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Add module"
          data-testid="module-picker-modal"
          onClick={handleClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(4, 6, 10, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 60,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 720,
              maxWidth: '92vw',
              maxHeight: '80vh',
              background: 'var(--cd-bg-raised)',
              border: '1px solid var(--cd-border-strong)',
              borderRadius: 'var(--cd-radius)',
              boxShadow: 'var(--cd-shadow-2)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              fontFamily: 'var(--cd-font-sans)',
              color: 'var(--cd-fg)',
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 18px',
                borderBottom: '1px solid var(--cd-border)',
                background: 'var(--cd-bg-card-header)',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--cd-font-mono)',
                  fontSize: 11,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}
              >
                Add module
              </span>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close add module picker"
                style={{
                  width: 24,
                  height: 24,
                  background: 'transparent',
                  border: '1px solid var(--cd-border)',
                  borderRadius: 'var(--cd-radius)',
                  color: 'var(--cd-fg-muted)',
                  cursor: 'pointer',
                  fontFamily: 'var(--cd-font-mono)',
                  fontSize: 13,
                }}
              >
                ×
              </button>
            </div>

            {/* Body — categories + module list */}
            <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
              {/* Category sidebar */}
              <div
                role="tablist"
                aria-orientation="vertical"
                aria-label="Module categories"
                style={{
                  width: 180,
                  borderRight: '1px solid var(--cd-border)',
                  overflowY: 'auto',
                  background: 'var(--cd-bg-panel)',
                }}
              >
                {categories.map((cat) => {
                  const active = cat === activeCategory;
                  return (
                    <button
                      key={cat}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setActiveCategory(cat)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '8px 14px',
                        background: active ? 'var(--cd-bg-hover)' : 'transparent',
                        borderLeft: active
                          ? '2px solid var(--cd-sig-iris)'
                          : '2px solid transparent',
                        border: 'none',
                        borderRight: 'none',
                        borderTop: 'none',
                        borderBottom: 'none',
                        color: active ? 'var(--cd-fg)' : 'var(--cd-fg-secondary)',
                        fontFamily: 'var(--cd-font-mono)',
                        fontSize: 10.5,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        aria-hidden="true"
                        data-testid={`category-glyph-${cat}`}
                        style={{
                          display: 'inline-block',
                          width: 12,
                          marginRight: 8,
                          color: CATEGORY_VISUAL[cat].color,
                          fontSize: 11,
                          textTransform: 'none',
                          letterSpacing: 0,
                        }}
                      >
                        {CATEGORY_VISUAL[cat].glyph}
                      </span>
                      {CATEGORY_LABELS[cat]}
                    </button>
                  );
                })}
              </div>

              {/* Module list */}
              <ul
                aria-label={`${CATEGORY_LABELS[activeCategory]} modules`}
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflowY: 'auto',
                  listStyle: 'none',
                  margin: 0,
                  padding: 8,
                }}
              >
                {modulesInCategory.length === 0 ? (
                  <li
                    style={{
                      padding: '20px 16px',
                      color: 'var(--cd-fg-muted)',
                      fontSize: 12,
                      fontStyle: 'italic',
                    }}
                  >
                    No modules in this category yet — sprint-4 / sprint-5 fills this in.
                  </li>
                ) : (
                  modulesInCategory.map((entry) => (
                    <li key={entry.id}>
                      <button
                        type="button"
                        onClick={() => handleSelect(entry)}
                        aria-label={`Add ${entry.label}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                          padding: '10px 12px',
                          background: 'transparent',
                          border: 'none',
                          borderBottom: '1px solid var(--cd-seam)',
                          color: 'var(--cd-fg)',
                          fontFamily: 'var(--cd-font-sans)',
                          fontSize: 12.5,
                          textAlign: 'left',
                          cursor: 'pointer',
                        }}
                      >
                        <span
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            minWidth: 0,
                          }}
                        >
                          <span style={{ fontWeight: 500 }}>{entry.label}</span>
                          <span
                            data-testid={`module-subcategory-${entry.id}`}
                            style={{
                              fontFamily: 'var(--cd-font-mono)',
                              fontSize: 9,
                              color: 'var(--cd-fg-muted)',
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase',
                            }}
                          >
                            {entry.subcategory}
                          </span>
                        </span>
                        <span
                          data-testid={`module-size-${entry.id}`}
                          style={{
                            fontFamily: 'var(--cd-font-mono)',
                            fontSize: 9.5,
                            color: 'var(--cd-fg-muted)',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                            marginLeft: 8,
                          }}
                        >
                          {entry.defaultSize.w}×{entry.defaultSize.h}
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
